import { useState } from "react";
import { useAddValve, useRemoveValve, useRenameValve, type InspectionDetail } from "../../api/inspections";

/** Which valve the sections below are showing, each with its spares and
 * answered checks, so a glance says which valve still has an empty list.
 * Adding, renaming and removing a valve are behind "Manage valves": most
 * chambers hold one, and the tools were noise on every visit. The last valve
 * is never removed. */
export default function ValveStrip({ detail, onSelectValve }: { detail: InspectionDetail; onSelectValve: (valveId: number) => void }) {
  const id = detail.inspection.id;
  const add = useAddValve(id);
  const rename = useRenameValve(id);
  const remove = useRemoveValve(id);
  const [managing, setManaging] = useState(false);
  const [label, setLabel] = useState("");
  const [renaming, setRenaming] = useState<number | null>(null);
  const canEdit = detail.actions.can_edit;
  const active = detail.valves.find((v) => v.id === detail.active_valve_id) ?? null;
  const one = detail.valves.length === 1;
  return (
    <section className="valve-strip" aria-label="Valves">
      <span className="section-label">{one ? "Valve" : "Valves"}</span>
      {detail.valves.map((valve) =>
        renaming === valve.id ? (
          <form
            key={valve.id}
            className="valve-rename"
            onSubmit={(e) => {
              e.preventDefault();
              rename.mutate({ valveId: valve.id, label: label.trim() || null }, { onSuccess: () => setRenaming(null) });
            }}
          >
            <input className="input" aria-label={`Name for valve ${valve.seq}`} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`Valve ${valve.seq}`} autoFocus />
            <button type="submit" className="row-button" disabled={rename.isPending}>
              Save
            </button>
            <button type="button" className="row-button" onClick={() => setRenaming(null)}>
              Cancel
            </button>
          </form>
        ) : (
          <button key={valve.id} type="button" className="valve-pill" aria-pressed={valve.id === detail.active_valve_id} onClick={() => onSelectValve(valve.id)}>
            <span className="valve-pill-name">{valve.name}</span>
            <span className="valve-pill-meta">
              {valve.spares} spares · {valve.answered}/{valve.total_checks} checks
            </span>
          </button>
        ),
      )}
      {canEdit ? (
        <button type="button" className="row-button valve-manage" aria-expanded={managing} onClick={() => setManaging((m) => !m)}>
          {managing ? "Done" : one ? "More than one valve?" : "Manage valves"}
        </button>
      ) : null}
      {canEdit && managing ? (
        <span className="valve-tools">
          <button
            type="button"
            className="valve-pill add"
            disabled={add.isPending}
            onClick={() => add.mutate(null, { onSuccess: (d) => d.active_valve_id !== null && onSelectValve(d.active_valve_id) })}
          >
            + Add valve
          </button>
          {active && renaming === null ? (
            <>
              <button
                type="button"
                className="row-button"
                onClick={() => {
                  setLabel(active.label ?? "");
                  setRenaming(active.id);
                }}
              >
                Rename {active.name}
              </button>
              <button
                type="button"
                className="row-button danger"
                disabled={remove.isPending || one}
                title={one ? "An inspection keeps at least one valve" : undefined}
                onClick={() => {
                  if (window.confirm(`Remove ${active.name}, with its measurement, spares and checks?`)) {
                    remove.mutate(active.id);
                  }
                }}
              >
                Remove
              </button>
            </>
          ) : null}
        </span>
      ) : null}
      {add.isError ? <p className="notice bad">{add.error.message}</p> : null}
      {remove.isError ? <p className="notice bad">{remove.error.message}</p> : null}
      {rename.isError ? <p className="notice bad">{rename.error.message}</p> : null}
    </section>
  );
}
